import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { green, red } from '@mui/material/colors'
import Circle from '@mui/icons-material/Circle'
import { Tooltip as AntdTooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ColumnTitleWithTooltip } from 'UI/VerticalTable/columns'
import Icon from 'components/Icon'
import { useCopyToClipboard } from 'containers/VerticalPage/common/hooks'
import { NOT_AVAILABLE, UNKNOWN } from 'utils/constants'
import { parse } from 'date-fns'
import parseHtml from 'html-react-parser'
import { DATE_FORMAT_MMM_YYYY, formattedDate } from 'utils/formatters'

const DESCRIPTION_TRUNCATE_LENGTH = 56

interface ExperienceView {
  name: string
  jobLocation: string
  jurisdiction: string
  jobRole: string
  jobDepartment: string
  startDate: string
  endDate: string
  jobDesc: string
  isExecutiveRole: boolean
  canonicalUrl: string
  status: string
}

export type EmployeeRow = Partial<ExperienceView> & {
  experiences?: ExperienceView[]
  subRows?: ExperienceView[]
  employeeId?: number
  indexId?: string
}

const hasExperiences = (item: EmployeeRow): boolean =>
  Array.isArray(item?.experiences) && item.experiences.length > 0

const pickField = <K extends keyof ExperienceView>(
  item: EmployeeRow,
  key: K,
): ExperienceView[K] | undefined =>
  hasExperiences(item) ? item.experiences![0]?.[key] : (item[key] as ExperienceView[K] | undefined)

const LinkedInProfileCell = ({ url }: { url?: string }) => {
  const { handleCopy, isLinkCopied } = useCopyToClipboard()

  if (!url) return <Typography>{NOT_AVAILABLE}</Typography>

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleCopy(url)
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        component='a'
        href={url}
        onClick={handleLinkClick}
        sx={{ color: 'rgba(34, 87, 197, 1)', cursor: 'pointer' }}
      >
        LinkedIn Profile
      </Typography>
      <AntdTooltip title={isLinkCopied ? 'Link copied' : 'Copy link'}>
        <Box
          onClick={handleCopyClick}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            p: 0.5,
            borderRadius: 1,
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          {isLinkCopied ? <Icon name='done' size='small' /> : <Icon name='copy' size='small' />}
        </Box>
      </AntdTooltip>
    </Box>
  )
}

const stripHtml = (html: string) =>
  html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const DescriptionCell = ({ description }: { description?: string }) => {
  const [showFull, setShowFull] = useState(false)

  if (!description) return null

  const plainText = stripHtml(description)

  if (plainText.length <= DESCRIPTION_TRUNCATE_LENGTH) return <Typography>{plainText}</Typography>

  return (
    <Box>
      <Typography component='div'>
        {showFull ? parseHtml(description) : `${plainText.substring(0, DESCRIPTION_TRUNCATE_LENGTH)}...`}
      </Typography>
      <Button
        onClick={e => {
          e.stopPropagation()
          setShowFull(prev => !prev)
        }}
        sx={{
          textTransform: 'none',
          color: 'rgba(34, 87, 197, 1)',
          fontFamily: 'Source Sans Pro',
          fontSize: '16px',
          fontWeight: 400,
          p: 0,
          '.MuiTouchRipple-root': { display: 'none' },
          '&:hover': { background: 'transparent' },
        }}
      >
        {showFull ? 'View less' : 'View more'}
      </Button>
    </Box>
  )
}

const calculatePeriod = (start?: string, end?: string) => {
  if (!start || !end) return ''

  const startDate = parse(start, 'yyyy/MM/dd', new Date())
  const endDate = parse(end, 'yyyy/MM/dd', new Date())

  const diffMonths = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44)

  const years = Math.floor(diffMonths / 12)
  const months = Math.floor(diffMonths % 12)

  const parts: string[] = []
  if (years > 0) parts.push(`${years} yr`)
  if (months > 0) parts.push(`${months} mos`)
  return parts.join(' ')
}

const titleCase = (text: string) =>
  text.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())

export const EMPLOYEES_COLUMN_KEYS = {
  name: 'Name',
  location: 'Location',
  jurisdiction: 'Jurisdiction',
  jobDepartment: 'JobDepartment',
  jobRole: 'JobRole',
  startDate: 'startDate',
  endDate: 'endDate',
  period: 'Period',
  jobDesc: 'JobDesc',
  status: 'Status',
  canonicalUrl: 'Canonical Url',
} as const

const defaultColumnIds: string[] = Object.values(EMPLOYEES_COLUMN_KEYS)

const useEmployeesColumns = (displayColumnIds: string[] = defaultColumnIds): ColumnsType<EmployeeRow> =>
  useMemo<ColumnsType<EmployeeRow>>(() => {
    const allColumns: ColumnsType<EmployeeRow> = [
      {
        key: EMPLOYEES_COLUMN_KEYS.name,
        title: 'Name',
        width: 200,
        render: (_value, item) => {
          const name = pickField(item, 'name') ?? ''
          const isExecutive = hasExperiences(item) && item.experiences!.some(exp => exp.isExecutiveRole)

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>{name}</Typography>
              {isExecutive && (
                <AntdTooltip title='Executive Employee'>
                  <Box sx={{ display: 'inline-flex' }}>
                    <Icon name='businessman' size='regular' />
                  </Box>
                </AntdTooltip>
              )}
            </Box>
          )
        },
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.location,
        title: (
          <ColumnTitleWithTooltip
            title='Location'
            tooltipText='Location data is raw input provided by the user on LinkedIn.'
          />
        ),
        width: 160,
        render: (_value, item) => pickField(item, 'jobLocation') || UNKNOWN,
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.jurisdiction,
        title: (
          <ColumnTitleWithTooltip
            title='Jurisdiction'
            tooltipText='Jurisdiction classification is derived from the raw location data using IVIX models.'
          />
        ),
        width: 160,
        render: (_value, item) => pickField(item, 'jurisdiction') || UNKNOWN,
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.jobDepartment,
        title: 'Department',
        width: 180,
        render: (_value, item) => pickField(item, 'jobDepartment') || NOT_AVAILABLE,
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.jobRole,
        title: 'Job Role',
        width: 240,
        render: (_value, item) => {
          const role = pickField(item, 'jobRole')
          return role ? titleCase(role) : NOT_AVAILABLE
        },
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.startDate,
        title: 'Start Date',
        width: 130,
        render: (_value, item) => {
          const field = pickField(item, 'startDate')
          return field ? formattedDate(field, DATE_FORMAT_MMM_YYYY) : NOT_AVAILABLE
        },
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.endDate,
        title: 'End Date',
        width: 130,
        render: (_value, item) => {
          const startDate = pickField(item, 'startDate')
          const endDate = pickField(item, 'endDate')
          if (!endDate && startDate) return 'Present'
          return endDate ? formattedDate(endDate, DATE_FORMAT_MMM_YYYY) : NOT_AVAILABLE
        },
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.period,
        title: 'Period',
        width: 120,
        render: (_value, item) => calculatePeriod(pickField(item, 'startDate'), pickField(item, 'endDate')),
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.jobDesc,
        title: 'Description',
        width: 320,
        render: (_value, item) => <DescriptionCell description={pickField(item, 'jobDesc')} />,
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.status,
        title: 'Status',
        width: 120,
        render: (_value, item) => {
          const field = pickField(item, 'status')
          if (!field) return null
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Circle sx={{ color: field === 'Active' ? green[500] : red[500], mr: 1, fontSize: 10 }} />
              <Typography>{field}</Typography>
            </Box>
          )
        },
      },
      {
        key: EMPLOYEES_COLUMN_KEYS.canonicalUrl,
        title: 'LinkedIn Profile URL',
        width: 220,
        render: (_value, item) => <LinkedInProfileCell url={pickField(item, 'canonicalUrl')} />,
      },
    ]

    return allColumns.filter(column => displayColumnIds.includes(column.key as string))
  }, [displayColumnIds])

export default useEmployeesColumns
